import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { ClientService } from '../../../services/client/client.service';
import { AuthenticationService, SignoutReason } from '../../../services/authentication/authentication.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { DeviceAuthService, DeviceAuth } from '../../../services/device-auth/device-auth.service';

const PIN_LENGTH = 6;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit, OnDestroy {

  configCode;
  clientConfig;
  clientConfigChanged: Subscription;

  signInForm: UntypedFormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  error;

  // PIN sign-in on an authorized device. 'pin' is offered whenever this
  // device holds a valid authorization; the password form is always one tap
  // away for users without a PIN (or when the authorization is gone).
  mode: 'password' | 'pin' = 'password';
  deviceAuth: DeviceAuth | null = null;
  pin = '';
  pinLength = PIN_LENGTH;
  pinDots = Array.from({ length: PIN_LENGTH }, (_, i) => i);
  pinError: string = null;
  pinShaking = false;
  pinValidating = false;
  notice: string = null;
  pinKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private clientService: ClientService,
    private deviceAuthService: DeviceAuthService,
  ) {
    if (authenticationService.isSignedIn()) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit() {
    this.clientConfigChanged = this.clientService.clientConfigChangedObservable().subscribe(clientConfig => {
      this.clientConfig = clientConfig;
      if (this.clientConfig && this.clientConfig.logoUrl) {
        console.log('UPDATE LOGO SOURCE');
        this.clientConfig.logoUrl = this.clientConfig.logoUrl.replace("white", "black");
      }
    });

    this.clientConfig = this.clientService.getConfig();
    if (this.clientConfig && this.clientConfig.logoUrl) {
      console.log('UPDATE LOGO SOURCE');
      this.clientConfig.logoUrl = this.clientConfig.logoUrl.replace("white", "black");
    }

    this.signInForm = this.formBuilder.group({
      email: ['', Validators.required],
      password: ['', Validators.required]
    });

    // Capture where the user was redirected from
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    this.applySignoutReason(this.authenticationService.consumeSignoutReason());
    this.refreshDeviceAuth();
  }

  ngOnDestroy() {
    if (this.clientConfigChanged) {
      this.clientConfigChanged.unsubscribe();
    }
  }

  get field() {
    return this.signInForm.controls;
  }

  onSubmit() {
    this.submitted = true;
    this.error = null;

    if (this.signInForm.invalid) {
      this.error = 'Email/Phone and password are required.';
      return;
    }

    this.loading = true;

    // Reload client config
    this.clientService.requestClientConfig()
    .then( () => {} )
    .finally( () => {
      this.authenticationService.signin(this.field.email.value, this.field.password.value)
        .then(() => {
          console.log('router navigate to root');
          this.router.navigate(['/']);
        })
        .catch(signinError => {
          this.loading = false;
          this.error = (signinError && signinError.error && signinError.error.msg) || 'Sign-in failed. Please try again.';
        });
      });
  }

  requestClientConfig() {
    this.clientService.requestClientConfig(this.configCode);
  }

  resetClientConfig() {
    this.clientService.resetClientConfig();
  }

  // --- PIN sign-in ------------------------------------------------------------

  private refreshDeviceAuth() {
    this.deviceAuth = this.deviceAuthService.isAuthorized() ? this.deviceAuthService.getDeviceAuth() : null;
    this.mode = this.deviceAuth ? 'pin' : 'password';
  }

  private applySignoutReason(reason: SignoutReason) {
    switch (reason) {
      case 'idle':
        this.notice = 'Locked after inactivity.';
        break;
      case 'relaunch':
        this.notice = null; // the PIN pad speaks for itself
        break;
      case 'expired':
        this.notice = 'Your session has expired.';
        break;
      case 'deviceRevoked':
        // The server rejected the device mid-session; the local copy is no
        // longer worth keeping, so fall straight back to the password form.
        this.deviceAuthService.clear();
        this.notice = 'This device is no longer authorized for PIN sign-in. Sign in with your password.';
        break;
      case 'authorized':
        this.notice = 'Device authorized. Users at ' +
          ((this.deviceAuthService.getDeviceAuth() || {} as any).locationName || 'this location') +
          ' can now sign in with their PIN.';
        break;
      default:
        this.notice = null;
    }
  }

  usePassword() {
    this.mode = 'password';
    this.pin = '';
    this.pinError = null;
  }

  usePin() {
    this.refreshDeviceAuth();
    this.error = null;
  }

  pressDigit(digit: string) {
    if (this.pinValidating || this.mode !== 'pin') {
      return;
    }
    this.pinError = null;
    if (this.pin.length < this.pinLength) {
      this.pin += digit;
    }
    if (this.pin.length === this.pinLength) {
      this.submitPin();
    }
  }

  deleteDigit() {
    if (this.pinValidating) {
      return;
    }
    this.pinError = null;
    this.pin = this.pin.slice(0, -1);
  }

  // Handheld scanners have physical keypads; phones may have a keyboard up.
  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.mode !== 'pin' || this.pinValidating) {
      return;
    }
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      this.pressDigit(event.key);
    } else if (event.key === 'Backspace') {
      event.preventDefault();
      this.deleteDigit();
    } else if (event.key === 'Enter' && this.pin.length > 0) {
      event.preventDefault();
      this.submitPin();
    } else if (event.key === 'Escape') {
      this.pin = '';
    }
  }

  async submitPin() {
    if (this.pinValidating || this.pin.length === 0) {
      return;
    }
    this.pinValidating = true;
    this.pinError = null;
    this.notice = null;
    const pin = this.pin;

    try {
      await this.deviceAuthService.pinIn(pin);
      this.router.navigate(['/']);
    } catch (err) {
      const body = (err && err.error) || {};
      this.pin = '';
      this.pinValidating = false;

      if (!this.deviceAuthService.isAuthorized()) {
        // Revoked, expired, disabled, or too many bad PINs: the device
        // authorization is gone, so only the password form remains.
        this.deviceAuth = null;
        this.mode = 'password';
        this.notice = body.msg || 'This device is no longer authorized for PIN sign-in. Sign in with your password.';
        return;
      }

      let message = body.msg || (err && err.status === 0 ? 'No connection. Check the network and try again.' : 'Sign-in failed.');
      if (typeof body.attemptsRemaining === 'number' && body.attemptsRemaining <= 2) {
        message += ' ' + body.attemptsRemaining + (body.attemptsRemaining === 1 ? ' attempt' : ' attempts') +
          ' left before this device must be authorized again.';
      }
      this.pinError = message;
      this.pinShaking = true;
      setTimeout(() => { this.pinShaking = false; }, 500);
    }
  }

}
